import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  BadgeCheck,
  Building2,
  CircleCheck,
  ClipboardList,
  CreditCard,
  FileText,
  Leaf,
  Loader2,
  Lock,
  Shield,
} from "lucide-react";
import { fetchCart, fetchOrders, deductStockForOrder } from "../../store";
import { getCurrentIdToken } from "../../firebase/authHelpers";
import config from "../../../src/config.json";
import "./payment.css";

const iconProps = { size: 18, strokeWidth: 1.75 };
const titleIconProps = { size: 22, strokeWidth: 1.75 };

let apiPrefix = "/api";
let baseUrl = config.api.baseUrl || "http://localhost:8000"; // Fallback to localhost if not set
let apiUrl = `${baseUrl}${apiPrefix}`;

async function paymentAuthHeaders() {
  const headers = { "Content-Type": "application/json" };
  const token = await getCurrentIdToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

// Custom hook to load Razorpay script dynamically
const useRazorpayScript = () => {
  const loadScript = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  return loadScript;
};

// API helper: create DB + Razorpay order
const createOrder = async ({
  userId,
  billingInfo,
  shippingInfo,
  receipt,
  paymentMethod,
}) => {
  const response = await fetch(
    `${apiUrl}/payment/create-order`,
    {
      method: "POST",
      credentials: "include",
      headers: await paymentAuthHeaders(),
      body: JSON.stringify({
        userId,
        billingInfo,
        shippingInfo,
        receipt,
        paymentMethod,
      }),
    }
  );
  return response.json();
};

// API helper: verify payment
const verifyPayment = async ({
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
  dbOrderId,
}) => {
  const response = await fetch(
    `${apiUrl}/payment/verify-payment`,
    {
      method: "POST",
      credentials: "include",
      headers: await paymentAuthHeaders(),
      body: JSON.stringify({
        razorpay_order_id,
        razorpay_payment_id,
        razorpay_signature,
        dbOrderId,
      }),
    }
  );
  return response.json();
};

const PaymentComponent = (props) => {
  const location = useLocation();
  const {
    amount,
    currency = "INR",
    receipt,
    successRoute = "/order-success",
    errorRoute = "/payment-error",
    userId,
    shippingAddress,
    billingAddress,
  } = { ...location.state, ...props };

  const [loading, setLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loadRazorpayScript = useRazorpayScript();

  const loadingSteps = [
    { Icon: Loader2, text: "Initializing payment gateway..." },
    { Icon: Building2, text: "Connecting to Razorpay..." },
    { Icon: FileText, text: "Creating secure payment order..." },
    { Icon: CircleCheck, text: "Ready for payment!" },
  ];

  const handlePayment = async () => {
    if (!amount || amount <= 0) {
      const errorMsg = "Invalid amount provided";
      navigate(errorRoute, { state: { error: errorMsg } });
      return;
    }

    setOrderLoading(true);
    setLoading(true);
    setLoadingStep(0);

    try {
      // Step 1: Initialization
      setLoadingStep(1);
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Step 2: Load Razorpay SDK
      setLoadingStep(2);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) throw new Error("Razorpay SDK failed to load");

      // Step 3: Create backend order
      setLoadingStep(3);
      const orderData = await createOrder({
        userId,
        billingInfo: billingAddress,
        shippingInfo: shippingAddress,
        receipt,
      });

      if (!orderData.success)
        throw new Error(orderData.message || "Failed to create order");

      const { razorpayOrder, key_id, dbOrderId } = orderData;

      // Step 4: Setup Razorpay options
      const options = {
        key: key_id,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Sri Shiv Enterprises",
        description: "Sustainable bags for a better tomorrow",
        order_id: razorpayOrder.id,
        prefill: {
          name: shippingAddress?.fullName || "Customer",
          contact: shippingAddress?.phone || "+919999999999",
        },
        theme: { color: "#2c5530" },
        modal: {
          ondismiss: () => {
            const errorMsg = "Payment cancelled by user";
            navigate(errorRoute, { state: { error: errorMsg } });
            setLoading(false);
          },
        },
        handler: async (response) => {
          try {

            const verifyData = await verifyPayment({ ...response, dbOrderId });

            if (verifyData.success) {
              // Deduct stock after successful payment
              try {
                await dispatch(deductStockForOrder({ orderId: dbOrderId })).unwrap();
              } catch (stockError) {
                console.error('Stock deduction failed:', stockError);
                // Continue with order processing even if stock deduction fails
                // This can be handled by background jobs or manual intervention
              }

              dispatch(
                fetchOrders(
                  userId,
                //   orderId: dbOrderId,
                )
              ).then(() => {
                navigate(successRoute, {
                  state: {
                    orderId: dbOrderId,
                  },
                });
              })
            .then(() => dispatch(fetchCart(userId)));
            } else
              throw new Error(
                "Payment verification failed. Please contact support."
              );
          } catch (err) {
            console.error(err);
            navigate(errorRoute, { state: { error: err.message } });
          }
        },
      };

      // Step 5: Open Razorpay checkout
      setLoadingStep(4);
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment initialization error:", error);
      navigate(errorRoute, { state: { error: error.message } });
    } finally {
      setOrderLoading(false);
      setLoading(false);
      setLoadingStep(0);
    }
  };

  return (
    <div className="payment-container">
      <div className="payment-details">
        <div className="payment-title-row">
          <Shield {...titleIconProps} className="payment-title-icon" aria-hidden />
          <h2 className="payment-title">Secure Payment</h2>
        </div>

        <div className="payment-info">
          <div className="payment-info-row">
            <span className="payment-info-label">Amount:</span>
            <span className="payment-info-value">
              {currency} {amount}
            </span>
          </div>
          {receipt && (
            <div className="payment-info-row">
              <span className="payment-info-label">Receipt:</span>
              <span className="payment-info-value">{receipt}</span>
            </div>
          )}
          <div className="payment-info-row">
            <span className="payment-info-label">Currency:</span>
            <span className="payment-info-value">{currency}</span>
          </div>
        </div>

        {orderLoading && (
          <div className="order-loading-overlay">
            <div className="payment-spinner"></div>
            <div className="loading-text">Setting up your payment...</div>
            <div className="loading-steps">
              {loadingSteps.map((step, index) => {
                const { Icon } = step;
                return (
                  <div
                    key={index}
                    className={`loading-step ${
                      index < loadingStep
                        ? "completed"
                        : index === loadingStep
                        ? "active"
                        : ""
                    }`}
                  >
                    <span className="loading-step-icon" aria-hidden>
                      <Icon {...iconProps} className={index === loadingStep ? "payment-step-icon-spin" : undefined} />
                    </span>
                    <span>{step.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={handlePayment}
          disabled={loading || !amount || amount <= 0}
          className="payment-button"
        >
          {loading ? (
            orderLoading ? (
              "Preparing Payment..."
            ) : (
              "Processing..."
            )
          ) : (
            <>
              <CreditCard {...iconProps} className="payment-button-icon" aria-hidden />
              Pay Securely with Razorpay
            </>
          )}
        </button>

        <div className="payment-instructions">
          <div className="instructions-title">
            <ClipboardList {...iconProps} className="instructions-title-icon" aria-hidden />
            Payment Instructions
          </div>
          <ul className="instructions-list">
            <li className="instructions-item">
              <span className="instructions-icon" aria-hidden>
                <Lock {...iconProps} />
              </span>
              <span>
                Click "Pay Securely" to open Razorpay's secure payment gateway
              </span>
            </li>
            <li className="instructions-item">
              <span className="instructions-icon" aria-hidden>
                <CreditCard {...iconProps} />
              </span>
              <span>
                Choose from UPI, cards, net banking, or wallet options
              </span>
            </li>
            <li className="instructions-item">
              <span className="instructions-icon" aria-hidden>
                <CircleCheck {...iconProps} />
              </span>
              <span>
                Complete payment and you'll be redirected to confirmation page
              </span>
            </li>
            <li className="instructions-item">
              <span className="instructions-icon" aria-hidden>
                <Leaf {...iconProps} />
              </span>
              <span>
                Your eco-friendly purchase helps save our environment!
              </span>
            </li>
          </ul>
        </div>

        {/* Security Information */}
        <div className="security-info">
          <div className="security-badge">
            <span className="security-icon" aria-hidden>
              <Lock {...iconProps} />
            </span>
            <span>SSL Secured</span>
          </div>
          <div className="security-badge">
            <span className="security-icon" aria-hidden>
              <Shield {...iconProps} />
            </span>
            <span>Bank Grade Security</span>
          </div>
          <div className="security-badge">
            <span className="security-icon" aria-hidden>
              <BadgeCheck {...iconProps} />
            </span>
            <span>PCI Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentComponent;
